import { motion } from 'framer-motion';
import { Diamond, Play, Users, Gift, Calendar, ExternalLink } from 'lucide-react';

const ExperiencePage = () => {
  const highlights = [
    {
      icon: Play,
      title: 'Live Streaming',
      description: 'Regular gameplay streams across multiple online casino platforms, bringing entertainment and excitement to viewers.'
    },
    {
      icon: Gift,
      title: 'Giveaways & Promotions',
      description: 'Hosting engaging giveaways that reward community members and create buzz around partner brands.'
    },
    {
      icon: Users,
      title: 'Community Building',
      description: 'Created and grown "The Relatable Family" - a thriving community of players who support each other.'
    },
    {
      icon: Calendar,
      title: 'Consistent Content',
      description: 'Regular streaming schedule with dedicated time slots for different casino platforms and games.'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <div className="min-h-screen pt-24 pb-16 px-4" data-testid="experience-page">
      {/* Decorative blobs */}
      <div className="glow-blob glow-blob-purple top-40 -right-48 fixed" />
      <div className="glow-blob glow-blob-green top-96 -left-48 fixed" />

      <div className="max-w-7xl mx-auto relative">
        {/* Header */}
        <motion.section
          className="text-center mb-16"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={itemVariants} className="flex items-center justify-center gap-2 mb-4">
            <Diamond className="w-5 h-5 text-[#39FF14] sparkle" />
            <span className="text-[#39FF14] font-medium uppercase tracking-wider text-sm">Experience</span>
            <Diamond className="w-5 h-5 text-[#39FF14] sparkle" />
          </motion.div>

          <motion.h1 
            variants={itemVariants}
            className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase mb-6"
          >
            <span className="text-[#E0E0E0]">Social Casino</span>
            <br />
            <span className="text-glow-purple text-[#9D4EDD]">Influencer & Streamer</span>
          </motion.h1>

          <motion.p 
            variants={itemVariants}
            className="text-lg text-gray-400 max-w-3xl mx-auto leading-relaxed"
          >
            I stream gameplay for multiple online casinos, host exciting giveaways, and bring together 
            a vibrant community of players called <span className="text-[#39FF14] font-semibold">"The Relatable Family"</span>. 
            Join us for entertainment, wins, and good vibes!
          </motion.p>
        </motion.section>

        {/* Featured Image */}
        <motion.section
          className="mb-16"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.div 
            variants={itemVariants}
            className="grid md:grid-cols-2 gap-8 items-center"
          >
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_ugc-marketing-brand/artifacts/5ozl958l_ChatGPT%20Image%20Dec%2018%2C%202025%2C%2005_36_06%20PM.png"
                alt="RelatableShayla Streaming"
                className="w-full rounded-2xl image-glow"
                data-testid="streaming-image"
              />
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 text-[#E0E0E0]">
                Bringing Energy to Every Stream
              </h2>
              <p className="text-gray-400 leading-relaxed mb-6">
                My streams are more than just gameplay - they're experiences. I bring authenticity, 
                energy, and genuine connection to every broadcast. Whether it's celebrating wins, 
                navigating losses, or just chatting with the community, I keep it real and relatable.
              </p>
              <div className="flex items-center gap-4">
                <img
                  src="https://customer-assets.emergentagent.com/job_ugc-marketing-brand/artifacts/0mfxwa09_ChatGPT%20Image%20Nov%2012%2C%202025%2C%2002_25_39%20PM.png"
                  alt="RelatableShayla Avatar"
                  className="w-16 h-16 rounded-full border-2 border-[#39FF14]"
                  data-testid="avatar-image"
                />
                <div>
                  <p className="font-semibold text-[#E0E0E0]">The Relatable Family</p>
                  <p className="text-sm text-gray-400">Join the community!</p>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.section>

        {/* YouTube Video Embed */}
        <motion.section
          className="mb-16"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.div variants={itemVariants} className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold uppercase mb-4">
              <span className="text-[#E0E0E0]">Watch Me</span>{' '}
              <span className="text-[#9D4EDD]">In Action</span>
            </h2>
            <p className="text-gray-400">Check out my latest content on YouTube</p>
          </motion.div>

          <motion.div 
            variants={itemVariants}
            className="video-container aspect-video max-w-4xl mx-auto"
            data-testid="youtube-embed"
          >
            <iframe
              width="100%"
              height="100%"
              src="https://www.youtube.com/embed/imWftKZDg-8"
              title="RelatableShayla YouTube Video"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              className="rounded-xl"
            />
          </motion.div>
        </motion.section>

        {/* Stream Schedule */}
        <motion.section
          className="mb-16"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.div variants={itemVariants} className="glass-card p-8 text-center">
            <img
              src="https://customer-assets.emergentagent.com/job_ugc-marketing-brand/artifacts/uod3rz7r_ChatGPT_Image_Aug_12_2025_03_11_10_AM-2.png"
              alt="Stream Schedule"
              className="w-full max-w-md mx-auto rounded-xl mb-6"
              data-testid="schedule-image"
            />
            <h3 className="text-xl font-bold text-[#E0E0E0] mb-2">Catch Me Live!</h3>
            <p className="text-gray-400">Tuesday Night Takeover - 8PM EST</p>
          </motion.div>
        </motion.section>

        {/* PlayFame Section */}
        <motion.section
          className="mb-16"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.div variants={itemVariants} className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold uppercase mb-4">
              <span className="text-[#E0E0E0]">Play With Me On</span>{' '}
              <span className="text-[#9D4EDD]">PlayFame</span>
            </h2>
            <p className="text-gray-400">Join me for slots, giveaways, and fun times!</p>
          </motion.div>

          <motion.div 
            variants={itemVariants}
            className="glass-card overflow-hidden max-w-4xl mx-auto"
            data-testid="playfame-section"
          >
            <div className="grid md:grid-cols-2 gap-0">
              <div className="aspect-square md:aspect-auto">
                <img
                  src="https://customer-assets.emergentagent.com/job_ugc-marketing-brand/artifacts/vy6scncj_52e601cad9acb2b998aaf11a64b567c2_0.jpeg"
                  alt="PlayFame with RelatableShayla"
                  className="w-full h-full object-cover"
                  data-testid="playfame-image"
                />
              </div>
              <div className="p-8 flex flex-col justify-center">
                <h3 className="text-2xl font-bold text-[#E0E0E0] mb-4">Join Me Live on PlayFame!</h3>
                <p className="text-gray-400 mb-6 leading-relaxed">
                  Come hang out with me on PlayFame where we play slots together, host exciting giveaways, 
                  and have an amazing time as a community. Follow my profile to never miss a stream!
                </p>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6 w-fit"
                  style={{ backgroundColor: 'rgba(157, 78, 221, 0.2)', color: '#9D4EDD' }}
                >
                  <Gift className="w-4 h-4" />
                  Live 2PM ET on Kick
                </div>
                <a
                  href="https://www.playfame.com/@relatableshayla"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary px-8 py-3 rounded-full font-semibold inline-flex items-center justify-center gap-2 w-fit"
                  data-testid="playfame-link"
                >
                  Visit My PlayFame
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>
          </motion.div>
        </motion.section>

        {/* Highlights Grid */}
        <motion.section
          className="mb-16"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.div variants={itemVariants} className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold uppercase mb-4">
              <span className="text-[#E0E0E0]">What Sets Me</span>{' '}
              <span className="text-[#39FF14]">Apart</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {highlights.map((item, index) => (
              <motion.div
                key={item.title}
                variants={itemVariants}
                className="service-card p-6"
                data-testid={`highlight-${index}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#9D4EDD]/20 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-6 h-6 text-[#9D4EDD]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold mb-2 text-[#E0E0E0]">{item.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Discord CTA */}
        <motion.section
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.div 
            variants={itemVariants}
            className="glass-card p-12 text-center"
          >
            <div className="w-20 h-20 rounded-full bg-[#5865F2]/20 flex items-center justify-center mx-auto mb-6">
              <svg viewBox="0 0 24 24" className="w-10 h-10 text-[#5865F2]" fill="currentColor">
                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
              </svg>
            </div>
            
            <h2 className="text-2xl sm:text-3xl font-bold uppercase mb-4">
              <span className="text-[#E0E0E0]">Join</span>{' '}
              <span className="text-[#5865F2]">The Relatable Family</span>
            </h2>
            
            <p className="text-gray-400 max-w-2xl mx-auto mb-8">
              Connect with fellow community members, get notified about streams, participate in giveaways, 
              and be part of something special. We're more than just viewers - we're family!
            </p>

            <a
              href="https://discord.gg/FEwaZfwnN5"
              target="_blank"
              rel="noopener noreferrer"
              className="discord-btn vibrate-hover inline-flex"
              data-testid="discord-join-btn"
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
              </svg>
              Join Our Discord
              <ExternalLink className="w-4 h-4" />
            </a>
          </motion.div>
        </motion.section>
      </div>
    </div>
  );
};

export default ExperiencePage;
